import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useList } from '@refinedev/core';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  FileText,
  User as UserIcon,
  ArrowRight,
  Check,
  X,
  Eye,
  Heart,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { apiClient } from '@/providers/api-client';
import { resolveFileUrl } from '@/lib/file-url';
import { type Teacher, getTeacherName } from './types';

// ─── Types ──────────────────────────────────────────────────

interface TeacherRelations {
  user: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    photoUrl: string | null;
  } | null;
  coursesCount: number;
  postsCount: number;
}

interface TeacherCourseRecord {
  id: number;
  slug: string;
  courseType: string;
  isPublished: boolean;
  thumbnailUrl: string | null;
  createdAt: string;
  title: string | null;
}

interface TeacherPostRecord {
  id: number;
  contentPreview: string;
  status: string;
  viewsCount: number;
  likesCount: number;
  commentsCount: number;
  createdAt: string;
}

interface TeacherCoursesDialogProps {
  teacher: Teacher | null;
  defaultTab?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReassigned?: () => void;
}

// ─── Constants ──────────────────────────────────────────────

const COURSE_TYPE_LABELS: Record<string, string> = {
  ONLINE: 'Онлайн',
  OFFLINE: 'Офлайн',
  HYBRID: 'Гибрид',
  LIVE: 'Live',
  SELF_PACED: 'Самост.',
  MEETING: 'Встреча',
  WEBINAR: 'Вебинар',
};

const POST_STATUS_LABELS: Record<string, string> = {
  PUBLISHED: 'Опубликован',
  DRAFT: 'Черновик',
  PENDING: 'На модерации',
  REJECTED: 'Отклонён',
};

const POSTS_PAGE_SIZE = 10;

// ─── Component ──────────────────────────────────────────────

export function TeacherCoursesDialog({ teacher, defaultTab = 'courses', open, onOpenChange, onReassigned }: TeacherCoursesDialogProps) {
  const queryClient = useQueryClient();
  const [reassigning, setReassigning] = useState<number | null>(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>('');
  const [postsPage, setPostsPage] = useState(1);

  const teacherId = teacher?.id;

  // Связи учителя (сводка)
  const { data: relations } = useQuery({
    queryKey: ['teachers', teacherId, 'relations'],
    queryFn: () => apiClient.get<TeacherRelations>(`/teachers/${teacherId}/relations`),
    enabled: open && teacherId !== null && teacherId !== undefined,
  });

  // Курсы учителя
  const { data: courses = [], isLoading: isLoadingCourses } = useQuery({
    queryKey: ['teachers', teacherId, 'courses'],
    queryFn: () => apiClient.get<TeacherCourseRecord[]>(`/teachers/${teacherId}/courses`),
    enabled: open && teacherId !== null && teacherId !== undefined,
  });

  // Посты учителя
  const { data: posts = [], isLoading: isLoadingPosts } = useQuery({
    queryKey: ['teachers', teacherId, 'posts', postsPage],
    queryFn: () => apiClient.get<TeacherPostRecord[]>(`/teachers/${teacherId}/posts?page=${postsPage}&limit=${POSTS_PAGE_SIZE}`),
    enabled: open && teacherId !== null && teacherId !== undefined,
  });

  // Все учителя для переназначения
  const { result: teachersResult } = useList<Teacher>({
    resource: 'teachers',
    pagination: { currentPage: 1, pageSize: 100, mode: 'server' },
    queryOptions: {
      enabled: open && reassigning !== null,
    },
  });

  const reassignMutation = useMutation({
    mutationFn: ({ courseId, toTeacherId }: { courseId: number; toTeacherId: number }) =>
      apiClient.post(`/teachers/${teacherId}/courses/${courseId}/reassign`, { toTeacherId }),
    onSuccess: () => {
      setReassigning(null);
      setSelectedTeacherId('');
      void queryClient.invalidateQueries({ queryKey: ['teachers', teacherId, 'courses'] });
      void queryClient.invalidateQueries({ queryKey: ['teachers', teacherId, 'relations'] });
      onReassigned?.();
    },
  });

  const postsTotal = relations?.postsCount ?? 0;
  const postsTotalPages = Math.ceil(postsTotal / POSTS_PAGE_SIZE);
  const allTeachers = (teachersResult?.data ?? []).filter((t) => t.id !== teacherId);

  const handleReassign = (courseId: number) => {
    if (!selectedTeacherId || !teacherId) {return;}
    reassignMutation.mutate({ courseId, toTeacherId: parseInt(selectedTeacherId, 10) });
  };

  const handleCancel = () => {
    setReassigning(null);
    setSelectedTeacherId('');
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) {handleCancel();} }}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Связи — {teacher ? getTeacherName(teacher) : ''}
          </DialogTitle>
        </DialogHeader>

        {/* Сводка счётчиков */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <UserIcon className="h-3.5 w-3.5" />
              <span className="text-xs">Пользователь</span>
            </div>
            <p className="text-sm font-semibold">
              {relations?.user ? (
                <span className="text-green-600">Привязан</span>
              ) : (
                <span className="text-muted-foreground">Нет</span>
              )}
            </p>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <BookOpen className="h-3.5 w-3.5" />
              <span className="text-xs">Курсы</span>
            </div>
            <p className="text-lg font-semibold">{relations?.coursesCount ?? 0}</p>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <FileText className="h-3.5 w-3.5" />
              <span className="text-xs">Посты</span>
            </div>
            <p className="text-lg font-semibold">{relations?.postsCount ?? 0}</p>
          </div>
        </div>

        {/* Табы */}
        <Tabs key={defaultTab} defaultValue={defaultTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="w-full">
            <TabsTrigger value="user" className="flex-1">
              <UserIcon className="h-4 w-4 mr-1.5" />
              Пользователь
            </TabsTrigger>
            <TabsTrigger value="courses" className="flex-1">
              <BookOpen className="h-4 w-4 mr-1.5" />
              Курсы
              {courses.length > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">{courses.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="posts" className="flex-1">
              <FileText className="h-4 w-4 mr-1.5" />
              Посты
              {postsTotal > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">{postsTotal}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Вкладка: Пользователь */}
          <TabsContent value="user" forceMount className="flex-1 overflow-y-auto data-[state=inactive]:hidden">
            {relations?.user ? (
              <div className="flex items-center gap-3 p-3 rounded-lg border">
                {relations.user.photoUrl ? (
                  <img src={relations.user.photoUrl} alt="" className="h-12 w-12 rounded-full object-cover" />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                    <UserIcon className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium">
                    {[relations.user.firstName, relations.user.lastName].filter(Boolean).join(' ') || `User #${relations.user.id}`}
                  </p>
                  {relations.user.username && (
                    <p className="text-sm text-muted-foreground">@{relations.user.username}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">ID: {relations.user.id}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                Пользователь не привязан
              </div>
            )}
          </TabsContent>

          {/* Вкладка: Курсы */}
          <TabsContent value="courses" forceMount className="flex-1 overflow-y-auto data-[state=inactive]:hidden">
            {isLoadingCourses ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">Загрузка...</div>
            ) : courses.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">Нет привязанных курсов</div>
            ) : (
              <div className="divide-y">
                {courses.map((c) => (
                  <div key={c.id} className="py-3 px-1">
                    <div className="flex items-center gap-3">
                      {c.thumbnailUrl ? (
                        <img src={resolveFileUrl(c.thumbnailUrl)} alt="" loading="lazy" className="h-10 w-16 object-cover rounded flex-shrink-0" />
                      ) : (
                        <div className="h-10 w-16 bg-muted rounded flex items-center justify-center flex-shrink-0">
                          <BookOpen className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.title ?? `#${c.id}`}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant={c.isPublished ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">
                            {c.isPublished ? 'Опубликован' : 'Черновик'}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {COURSE_TYPE_LABELS[c.courseType] ?? c.courseType}
                          </span>
                        </div>
                      </div>
                      {reassigning === c.id ? (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Select value={selectedTeacherId} onValueChange={setSelectedTeacherId}>
                            <SelectTrigger className="w-[140px] h-8 text-xs">
                              <SelectValue placeholder="Учитель..." />
                            </SelectTrigger>
                            <SelectContent>
                              {allTeachers.map((t) => (
                                <SelectItem key={t.id} value={String(t.id)}>{getTeacherName(t)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={!selectedTeacherId || reassignMutation.isPending} onClick={() => handleReassign(c.id)}>
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleCancel}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" className="text-xs flex-shrink-0" onClick={() => setReassigning(c.id)}>
                          <ArrowRight className="h-3.5 w-3.5 mr-1" />
                          Сменить
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Вкладка: Посты */}
          <TabsContent value="posts" forceMount className="flex-1 overflow-y-auto flex flex-col min-h-0 data-[state=inactive]:hidden">
            {!relations?.user ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">Пользователь не привязан — постов нет</div>
            ) : isLoadingPosts ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">Загрузка...</div>
            ) : posts.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">Нет постов</div>
            ) : (
              <>
                <div className="divide-y flex-1">
                  {posts.map((p) => (
                    <div key={p.id} className="py-2.5 px-1">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{p.contentPreview || `Пост #${p.id}`}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge variant={p.status === 'PUBLISHED' ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">
                              {POST_STATUS_LABELS[p.status] ?? p.status}
                            </Badge>
                            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                              <Eye className="h-3 w-3" /> {p.viewsCount}
                            </span>
                            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                              <Heart className="h-3 w-3" /> {p.likesCount}
                            </span>
                            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                              <MessageSquare className="h-3 w-3" /> {p.commentsCount}
                            </span>
                          </div>
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap mt-0.5">
                          {formatDate(p.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {postsTotalPages > 1 && (
                  <div className="flex items-center justify-between pt-2 border-t">
                    <Button variant="outline" size="sm" disabled={postsPage <= 1} onClick={() => setPostsPage((p) => Math.max(1, p - 1))}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{postsPage} / {postsTotalPages}</span>
                    <Button variant="outline" size="sm" disabled={postsPage >= postsTotalPages} onClick={() => setPostsPage((p) => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
